import { useState } from 'react';
import {
  CheckItem,
  DisplayHeading,
  Eyebrow,
  INK,
  MUTED,
  PAPER_CARD,
  Reveal,
} from './ui';
import { planCreditsSummary } from '../../helpers/aiCreditCosts';

// Prices must stay in sync with components/About/Join/PricingTable.js
const PLANS = [
  {
    name: 'Starter',
    tag: 'COMMUNITY',
    monthly: null,
    yearly: 19,
    yearlyIsTotal: true,
    description: 'Join the community and publish on TravelFeed.com.',
    features: [
      'Publish on TravelFeed.com',
      'Verified badge',
      'Cross-post to Hive',
      planCreditsSummary('starter'),
      'Ad-free experience',
    ],
    cta: 'Join the community',
  },
  {
    name: 'Basic',
    monthly: 19,
    yearly: 15,
    description: 'Your own standalone blog, ready for the world.',
    features: [
      'Everything in Starter',
      'Your own standalone blog',
      'Connect your own domain',
      'Custom design & branding',
      planCreditsSummary('basic'),
      'Free SSL & global CDN',
    ],
    cta: 'Start your blog',
  },
  {
    name: 'Pro',
    monthly: 29,
    yearly: 25,
    isPopular: true,
    // Matches the standing signup offer (showProDiscount in /join/plan)
    firstYearDiscount: 0.5,
    description: 'For bloggers who mean business.',
    features: [
      'Everything in Basic',
      'Interactive world map',
      'Analytics dashboard',
      'Site search & dark mode',
      planCreditsSummary('pro'),
    ],
    cta: 'Start your blog',
  },
  {
    name: 'VIP',
    monthly: 49,
    yearly: 45,
    description: 'For established blogs that want it all.',
    features: [
      'Everything in Pro',
      'API access',
      'Dedicated support',
      'Priority feature requests',
      planCreditsSummary('vip'),
    ],
    cta: 'Start your blog',
  },
];

export default function Pricing({ onJoin }) {
  const [yearly, setYearly] = useState(true);

  return (
    <section id="pricing" className="tw-scroll-mt-24 tw-py-20 sm:tw-py-28">
      <div className="tw-mx-auto tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <Reveal className="tw-mx-auto tw-max-w-3xl tw-text-center">
          <Eyebrow>Fares</Eyebrow>
          <DisplayHeading className="tw-text-4xl tw-leading-[1.1] sm:tw-text-5xl">
            Pick your fare.
          </DisplayHeading>
          <p className={`${MUTED} tw-mx-auto tw-mb-0 tw-mt-5 tw-max-w-xl tw-text-lg`}>
            Start with a community membership, or get your own standalone blog.
            Upgrade, downgrade or cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="tw-mt-8 tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-p-1">
            <button
              type="button"
              onClick={() => setYearly(false)}
              className={`tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-transition-colors ${
                !yearly
                  ? 'tw-bg-primary-600 tw-text-white'
                  : `tw-bg-transparent ${MUTED}`
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              className={`tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-transition-colors ${
                yearly
                  ? 'tw-bg-primary-600 tw-text-white'
                  : `tw-bg-transparent ${MUTED}`
              }`}
            >
              Yearly
              <span
                className={`tw-ml-2 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold ${
                  yearly
                    ? 'tw-bg-white/20 tw-text-white'
                    : 'tw-bg-amber-500/10 tw-text-amber-700 dark:tw-text-amber-400'
                }`}
              >
                SAVE $48
              </span>
            </button>
          </div>
        </Reveal>

        <div className="tw-mt-12 tw-grid tw-grid-cols-1 tw-items-stretch tw-gap-6 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {PLANS.map((plan, i) => {
            const basePrice = plan.yearlyIsTotal
              ? plan.yearly
              : yearly
              ? plan.yearly
              : plan.monthly;
            const price = plan.firstYearDiscount
              ? basePrice * (1 - plan.firstYearDiscount)
              : basePrice;
            const formattedPrice = Number.isInteger(price)
              ? price
              : price.toFixed(2);
            return (
              <Reveal key={plan.name} delay={i * 0.07} className="tw-h-full">
                <div
                  className={`tw-relative tw-flex tw-h-full tw-flex-col tw-rounded-2xl tw-p-7 ${
                    plan.isPopular
                      ? 'tw-border-2 tw-border-solid tw-border-primary-600 tw-bg-white dark:tw-bg-[#15231E] tw-shadow-[0_30px_60px_-24px_rgba(0,137,123,0.45)]'
                      : PAPER_CARD
                  }`}
                >
                  {plan.isPopular && (
                    <span className="tw-absolute -tw-top-3.5 tw-left-1/2 -tw-translate-x-1/2 tw-whitespace-nowrap tw-rounded-full tw-bg-primary-600 tw-px-4 tw-py-1.5 tw-font-mono tw-text-[10px] tw-font-bold tw-tracking-[0.2em] tw-text-white">
                      MOST POPULAR
                    </span>
                  )}
                  {plan.tag && (
                    <span className="tw-absolute tw-right-5 tw-top-5 tw-rounded-full tw-bg-[#F1ECDF] dark:tw-bg-[#26382F] tw-px-3 tw-py-1 tw-font-mono tw-text-[9px] tw-font-semibold tw-tracking-[0.2em] tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
                      {plan.tag}
                    </span>
                  )}
                  {plan.firstYearDiscount && (
                    <span className="tw-absolute tw-right-5 tw-top-5 tw-rounded-full tw-bg-amber-500/10 tw-px-3 tw-py-1 tw-font-mono tw-text-[9px] tw-font-bold tw-tracking-[0.2em] tw-text-amber-700 dark:tw-text-amber-400">
                      −50% FIRST YEAR
                    </span>
                  )}
                  <p className={`${INK} tw-mb-1 tw-mt-0 tw-text-lg tw-font-bold`}>
                    {plan.name}
                  </p>
                  <p className={`${MUTED} tw-mb-5 tw-mt-0 tw-min-h-[40px] tw-text-sm`}>
                    {plan.description}
                  </p>
                  <p className="tw-mb-0 tw-mt-0">
                    {plan.firstYearDiscount && (
                      <span className={`${MUTED} tw-mr-2 tw-text-xl tw-font-semibold tw-line-through`}>
                        ${basePrice}
                      </span>
                    )}
                    <span className={`${INK} tw-text-4xl tw-font-bold tw-tracking-tight`}>
                      ${formattedPrice}
                    </span>
                    <span className={`${MUTED} tw-ml-1.5 tw-text-sm`}>
                      {plan.yearlyIsTotal ? '/year' : '/month'}
                    </span>
                  </p>
                  <p className={`${MUTED} tw-mb-6 tw-mt-1 tw-min-h-[16px] tw-font-mono tw-text-[10px] tw-tracking-wider`}>
                    {plan.firstYearDiscount
                      ? yearly
                        ? `$${plan.yearly * 12 * (1 - plan.firstYearDiscount)} FIRST YEAR · THEN $${plan.yearly * 12}/YEAR`
                        : `50% OFF FIRST YEAR · THEN $${plan.monthly}/MONTH`
                      : plan.yearlyIsTotal
                      ? 'BILLED YEARLY'
                      : yearly
                      ? `$${plan.yearly * 12}/YEAR · BILLED YEARLY`
                      : 'BILLED MONTHLY'}
                  </p>
                  <ul className="tw-m-0 tw-flex tw-flex-1 tw-list-none tw-flex-col tw-gap-3 tw-p-0">
                    {plan.features.map(feature => (
                      <CheckItem key={feature}>{feature}</CheckItem>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={onJoin}
                    className={`tw-mt-7 tw-w-full tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-5 tw-py-3 tw-text-[15px] tw-font-semibold tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 ${
                      plan.isPopular
                        ? 'tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-shadow-[0_10px_30px_-10px_rgba(0,137,123,0.7)]'
                        : 'tw-bg-[#1D2D28] tw-text-white hover:tw-bg-black dark:tw-bg-[#EBE6D9] dark:tw-text-[#1D2D28] dark:hover:tw-bg-white'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="tw-mt-10 tw-text-center">
          <p className={`${MUTED} tw-m-0 tw-text-sm`}>
            All prices in USD. Use a free subdomain or connect your own domain.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
